import React, { useEffect, useState } from "react";
import {
    Sparkles,
    CheckCircle2,
    AlertCircle,
    Loader2,
    Save,
    Plug,
    Info,
} from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import HowThisWorks from "@/components/admin/HowThisWorks";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

export default function SettingsFundle() {
    const [form, setForm] = useState({
        fundle_base_url: "",
        fundle_userid: "",
        fundle_pwd: "",
        fundle_store_code: "ONLINE",
    });
    const [meta, setMeta] = useState({
        configured: false,
        fundle_pwd_set: false,
        fundle_pwd_last4: "",
        last_test_at: null,
        last_test_status: null,
        last_test_message: null,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testMobile, setTestMobile] = useState("9891331123");
    const [testResult, setTestResult] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/api/admin/settings/fundle");
            setForm({
                fundle_base_url: data.fundle_base_url || "",
                fundle_userid: data.fundle_userid || "",
                fundle_pwd: "", // never pre-fill from server
                fundle_store_code: data.fundle_store_code || "ONLINE",
            });
            setMeta({
                configured: data.configured,
                fundle_pwd_set: data.fundle_pwd_set,
                fundle_pwd_last4: data.fundle_pwd_last4 || "",
                last_test_at: data.last_test_at,
                last_test_status: data.last_test_status,
                last_test_message: data.last_test_message,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                fundle_base_url: form.fundle_base_url.trim(),
                fundle_userid: form.fundle_userid.trim(),
                fundle_store_code: form.fundle_store_code.trim() || "ONLINE",
            };
            // Only send password if user typed a new one
            if (form.fundle_pwd.trim()) payload.fundle_pwd = form.fundle_pwd.trim();
            await adminApi.put("/api/admin/settings/fundle", payload);
            toast.success("Fundle credentials saved");
            setForm((f) => ({ ...f, fundle_pwd: "" }));
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const test = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await adminApi.post("/api/admin/settings/fundle/test", {
                mobile: testMobile.trim() || "9891331123",
            });
            setTestResult(data);
            if (data.found) {
                toast.success(
                    `Connected — member ${data.info.name || ""} · ${data.info.balance} Petals`
                );
            } else {
                toast.success("Credentials valid — test mobile not a member (expected for some test numbers)");
            }
            load();
        } catch (e) {
            const detail = e.response?.data?.detail || "Test failed";
            setTestResult({ ok: false, error: detail });
            toast.error(detail);
            load();
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <div className="p-10 text-stone-500 flex items-center gap-2">
                <Loader2 className="animate-spin" size={16} /> Loading Fundle settings…
            </div>
        );
    }

    return (
        <div className="p-10 max-w-3xl" data-testid="page-settings-fundle">
            <PageHeader
                eyebrow="Settings"
                title="Fundle Loyalty (Petals)"
                description="API credentials Priya uses to look up Orchid Rewards members, fetch Petal balances, send OTPs and redeem points."
            />

            {/* Status banner */}
            <div
                className={`mb-6 border rounded-lg p-4 flex items-start gap-3 ${
                    meta.configured
                        ? meta.last_test_status === "connected"
                            ? "border-sage-500/40 bg-sage-500/5"
                            : "border-stone-200 bg-stone-50"
                        : "border-gold-500/40 bg-gold-500/5"
                }`}
                data-testid="fundle-status"
            >
                {meta.configured ? (
                    meta.last_test_status === "connected" || meta.last_test_status === "connected_no_member" ? (
                        <CheckCircle2 className="text-sage-700 mt-0.5 shrink-0" size={18} />
                    ) : meta.last_test_status === "failed" ? (
                        <AlertCircle className="text-red-700 mt-0.5 shrink-0" size={18} />
                    ) : (
                        <Info className="text-stone-500 mt-0.5 shrink-0" size={18} />
                    )
                ) : (
                    <AlertCircle className="text-gold-700 mt-0.5 shrink-0" size={18} />
                )}
                <div className="flex-1 text-sm">
                    {!meta.configured ? (
                        <>
                            <div className="font-medium text-stone-900">
                                Fundle is not configured yet.
                            </div>
                            <div className="text-stone-600 mt-0.5">
                                Priya will respond with &ldquo;Petals service is not yet configured&rdquo; whenever a guest asks about Rewards or Petals.
                            </div>
                        </>
                    ) : meta.last_test_status === "connected" ? (
                        <>
                            <div className="font-medium text-sage-700">
                                Connected and verified
                            </div>
                            <div className="text-stone-600 mt-0.5">{meta.last_test_message}</div>
                        </>
                    ) : meta.last_test_status === "failed" ? (
                        <>
                            <div className="font-medium text-red-700">Last test failed</div>
                            <div className="text-stone-600 mt-0.5">{meta.last_test_message}</div>
                        </>
                    ) : (
                        <>
                            <div className="font-medium text-stone-900">
                                Credentials saved — please test the connection.
                            </div>
                            {meta.last_test_message && (
                                <div className="text-stone-600 mt-0.5">{meta.last_test_message}</div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl p-6 mb-6">
                <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
                    <Sparkles size={16} className="text-orchid-700" /> API credentials
                </h3>
                <div className="space-y-4">
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                            Base URL
                        </label>
                        <input
                            value={form.fundle_base_url}
                            onChange={(e) =>
                                setForm({ ...form, fundle_base_url: e.target.value })
                            }
                            placeholder="https://api.fundle.in/v1.0"
                            data-testid="fundle-base-url"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 font-mono"
                        />
                        <p className="text-[11px] text-stone-500 mt-1">
                            The Fundle Loyalty API root, without trailing slash. Method names like
                            <code className="bg-stone-100 px-1 rounded mx-1">GET_CUSTOMER_TRANS_INFO</code>
                            are appended automatically.
                        </p>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                            User ID
                        </label>
                        <input
                            value={form.fundle_userid}
                            onChange={(e) =>
                                setForm({ ...form, fundle_userid: e.target.value })
                            }
                            placeholder="orchid-api-user"
                            data-testid="fundle-userid"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 font-mono"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                            Password{" "}
                            {meta.fundle_pwd_set && (
                                <span className="text-stone-400 normal-case tracking-normal">
                                    (currently ••••{meta.fundle_pwd_last4})
                                </span>
                            )}
                        </label>
                        <input
                            type="password"
                            value={form.fundle_pwd}
                            onChange={(e) =>
                                setForm({ ...form, fundle_pwd: e.target.value })
                            }
                            placeholder={
                                meta.fundle_pwd_set ? "Leave blank to keep current password" : "Enter password"
                            }
                            data-testid="fundle-pwd"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 font-mono"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                            Store code
                        </label>
                        <input
                            value={form.fundle_store_code}
                            onChange={(e) =>
                                setForm({ ...form, fundle_store_code: e.target.value })
                            }
                            placeholder="ONLINE"
                            data-testid="fundle-store-code"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 font-mono"
                        />
                        <p className="text-[11px] text-stone-500 mt-1">
                            Used as the issuing store for redemptions. Defaults to <code className="bg-stone-100 px-1 rounded">ONLINE</code>.
                        </p>
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="fundle-save"
                        className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
                    >
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        {saving ? "Saving…" : "Save credentials"}
                    </button>
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl p-6 mb-8">
                <h3 className="font-medium text-stone-900 mb-1 flex items-center gap-2">
                    <Plug size={16} className="text-orchid-700" /> Test the connection
                </h3>
                <p className="text-xs text-stone-500 mb-4">
                    Pings <code className="bg-stone-100 px-1 rounded">GET_CUSTOMER_TRANS_INFO</code> with a known mobile and reports back. Tests work even before saving — but you must save first to persist.
                </p>
                <div className="flex gap-2">
                    <input
                        value={testMobile}
                        onChange={(e) => setTestMobile(e.target.value)}
                        placeholder="Test mobile (e.g. 9891331123)"
                        data-testid="fundle-test-mobile"
                        className="flex-1 px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 font-mono"
                    />
                    <button
                        onClick={test}
                        disabled={testing || !meta.configured}
                        data-testid="fundle-test"
                        className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium px-5 py-2 rounded-md disabled:opacity-50"
                    >
                        {testing ? <Loader2 size={13} className="animate-spin" /> : <Plug size={13} />}
                        {testing ? "Testing…" : "Test connection"}
                    </button>
                </div>
                {!meta.configured && (
                    <p className="text-[11px] text-gold-700 mt-2">
                        Save your credentials before testing.
                    </p>
                )}

                {testResult && (
                    <div
                        className={`mt-4 rounded-md border p-3 text-sm ${
                            testResult.ok
                                ? "border-sage-500/30 bg-sage-500/5"
                                : "border-red-200 bg-red-50"
                        }`}
                        data-testid="fundle-test-result"
                    >
                        {testResult.ok ? (
                            testResult.found ? (
                                <>
                                    <div className="font-medium text-sage-700 flex items-center gap-1.5">
                                        <CheckCircle2 size={14} /> Connection works · member found
                                    </div>
                                    <ul className="mt-2 text-stone-700 space-y-0.5 text-xs">
                                        <li>
                                            <span className="text-stone-500">Name:</span>{" "}
                                            {testResult.info.name || "—"}
                                        </li>
                                        <li>
                                            <span className="text-stone-500">Tier:</span>{" "}
                                            {testResult.info.tier || "—"}
                                        </li>
                                        <li>
                                            <span className="text-stone-500">Balance:</span>{" "}
                                            <strong>{testResult.info.balance}</strong> Petals
                                        </li>
                                        {testResult.info.bonus > 0 && (
                                            <li>
                                                <span className="text-stone-500">Bonus:</span>{" "}
                                                <strong>{testResult.info.bonus}</strong> Petals
                                            </li>
                                        )}
                                        <li>
                                            <span className="text-stone-500">Valid till:</span>{" "}
                                            {testResult.info.valid_till || "—"}
                                        </li>
                                    </ul>
                                </>
                            ) : (
                                <div className="text-stone-700">
                                    <strong className="text-sage-700">Credentials valid.</strong>{" "}
                                    Test mobile <code>{testResult.tested_mobile}</code> isn&apos;t a registered Orchid Rewards member — that&apos;s OK, the API call returned cleanly.
                                </div>
                            )
                        ) : (
                            <div className="text-red-700 text-xs whitespace-pre-wrap">
                                {testResult.error}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <HowThisWorks
                steps={[
                    "Paste the Fundle API base URL, your user ID and password (from your Fundle account manager) and save.",
                    "Click Test connection with a known mobile (e.g. 9891331123) to verify Priya can reach Fundle.",
                    "Priya will then handle 'Know your Petals' and 'Redeem Petals' flows live with real balances and OTPs.",
                    "Rotate the password anytime — leaving the password field blank during save keeps the existing one.",
                ]}
            />
        </div>
    );
}
